import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import { ArrowLeft, Download, ZoomIn, X } from 'lucide-react';

// LifeCorp AI Marketing Materials
const marketingMaterials = {
  'lifecorp-ai': {
    title: 'LifeCorp AI Marketing Materials',
    description: 'Professional marketing assets showcasing LifeCorp AI features, benefits, and capabilities.',
    images: [
      {
        id: 'intelligence-hub',
        title: 'Intelligence Hub Overview',
        description: 'Complete visibility, AI-powered insights, and unified command center for your personal and business intelligence.',
        url: 'https://customer-assets.emergentagent.com/job_49cc5dea-90ec-4f4c-8db4-81b9b7270408/artifacts/m58bwxa8_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2010_14_37%20PM.png',
        category: 'Product Overview'
      },
      {
        id: 'board-room',
        title: 'Board Room Features',
        description: 'AI-powered meetings, unified decisions, real-time collaboration, and executive intelligence for better outcomes.',
        url: 'https://customer-assets.emergentagent.com/job_49cc5dea-90ec-4f4c-8db4-81b9b7270408/artifacts/4g902ryg_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2010_01_50%20PM.png',
        category: 'Board Room'
      },
      {
        id: 'executive-system',
        title: 'AI Executive Team',
        description: 'Your personal executive operating system - AI CEO, Memory Vault, Mission Control, and intelligent execution tools.',
        url: 'https://customer-assets.emergentagent.com/job_49cc5dea-90ec-4f4c-8db4-81b9b7270408/artifacts/atbh84tt_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2010_26_56%20PM.png',
        category: 'Executive Features'
      },
      {
        id: 'memory-vault',
        title: 'Memory Vault System',
        description: 'AI-powered memory intelligence - capture everything, forget nothing, and turn memories into actionable insights.',
        url: 'https://customer-assets.emergentagent.com/job_49cc5dea-90ec-4f4c-8db4-81b9b7270408/artifacts/h5kmclmg_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2010_29_11%20PM.png',
        category: 'Memory Features'
      }
    ]
  }
};

function MarketingMaterialsPage() {
  const { slug } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);
  const materials = marketingMaterials[slug];

  if (!materials) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Marketing Materials Not Found</h1>
            <Link to="/" className="text-blue-400 hover:text-blue-300">← Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <Header />
      
      <main className="container mx-auto px-4 py-16 max-w-7xl">
        {/* Header */}
        <div className="mb-12">
          <Link 
            to={`/apps/${slug}`}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {materials.title.split(' Marketing')[0]}
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {materials.title}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl">
            {materials.description}
          </p>
        </div>

        {/* Marketing Materials Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {materials.images.map((image) => (
            <div
              key={image.id}
              className="group bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all duration-300"
            >
              {/* Image */}
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-900">
                <img 
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                
                {/* Overlay on Hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                  <button
                    onClick={() => setSelectedImage(image)}
                    className="p-3 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <ZoomIn className="w-6 h-6 text-white" />
                  </button>
                  <a
                    href={image.url}
                    download={`lifecorp-${image.id}.png`}
                    className="p-3 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Download className="w-6 h-6 text-white" />
                  </a>
                </div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-blue-500/20 backdrop-blur-sm border border-blue-500/30 rounded-full text-xs font-medium text-blue-300">
                    {image.category}
                  </span>
                </div>
              </div>

              {/* Details */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  {image.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {image.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Download All Button */}
        <div className="mt-12 text-center">
          <p className="text-slate-400 mb-4">Need all marketing materials?</p>
          <button className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors">
            <Download className="w-5 h-5" />
            Download All Assets
          </button>
        </div>
      </main>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          <div className="max-w-6xl w-full">
            <img 
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">
                {selectedImage.title}
              </h3>
              <p className="text-slate-300">
                {selectedImage.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-slate-950 mt-16">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_c83bb9d1-4481-4216-be0b-e2d2e530a3a4/artifacts/j5gcpl1b_Peaceful%20harmony%20in%20a%20logo%20design.png"
              alt="Perfect Peace Enterprise Logo"
              className="h-10 w-auto object-contain"
            />
            <div>
              <div className="text-base font-semibold text-white">
                Perfect Peace Enterprise
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                Building digital products for real-world impact.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default MarketingMaterialsPage;
